import React, { useCallback, useState, useEffect } from 'react'
import { useNavigate, Link, useParams, useLocation } from 'react-router-dom'
import { openResource, openResources } from '../../api' // You might need to update this for Firebase Storage

import Columns from '../Columns'
import Info from '../Info'
import DataTable, { Row, RowData } from '../DataTable'
import DateTime from '../DateTime'
import ContextMenu from '../ContextMenu'
import EditIncidentStatus from '../EditIncidentStatus'
import DropdownMenu, { DropdownItem } from '../DropdownMenu'
import LocationRenderer from '../LocationRenderer'
import Spinner from '../Spinner'
import { fetchUserProfile, fetchUserProfiles } from '../../hooks/userProfiles'
import { IconPickerItem } from '../Form/IconField'
import { useApiToken, useError } from '../../hooks'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs
} from 'firebase/firestore'
import { auth, authedJsonHeaders, db, ENV_SUFFIX } from '../../firebase'
import { deleteIncidentMapMarker } from '../../hooks/firebaseService'
import toast from 'react-hot-toast'
import useGetUserName from '../../hooks/useGetUserData'
import useUser from '../../hooks/getUserName'
import { useOrganizationTypeName } from '../../hooks/useOrganizationTypeName'
import { X } from 'lucide-react'

function capitalize(s) {
  if (!s) return ''
  return `${s.slice(0, 1).toUpperCase()}${s.slice(1)}`
}

function DispatchOrganizationsList({ dispatchOrganizations }) {
  const navigate = useNavigate()

  dispatchOrganizations =
    dispatchOrganizations?.sort((a, b) => a.id - b.id) || []

  return (
    <div>
      <DataTable
        columns={['ID', 'Name', 'Off-Duty']}
        className='is-fullwidth is-hoverable'>
        {dispatchOrganizations.map(({ id, name, dispatchFlags, offDuty }) => (
          <Row key={id} onClick={() => navigate(`/organizations/${id}`)}>
            <RowData>{id}</RowData>
            <RowData>{name}</RowData>
            <RowData>{offDuty ? 'Yes' : 'No'}</RowData>
            {/* <RowData>{!!(dispatchFlags & (1 << 0)) ? 'Yes' : 'No'}</RowData> */}
          </Row>
        ))}
      </DataTable>
    </div>
  )
}

function MapMarkersList({ incidentId, mapMarkers, refreshData }) {
  const setError = useError()

  mapMarkers = mapMarkers?.sort((a, b) => a.id - b.id) || []

  const deleteMarker = useCallback(
    async (markerId) => {
      try {
        await deleteIncidentMapMarker(incidentId, markerId)
        toast.success('map marker delete successfull')
        refreshData()
      } catch (err) {
        setError(err)
      }
    },
    [setError, refreshData, incidentId]
  )

  return (
    <div>
      <DataTable
        columns={['ID', 'Label', 'Location', 'Actions']}
        className='is-fullwidth is-hoverable'>
        {mapMarkers.map((marker) => (
          <Row key={marker.id}>
            <RowData>{marker.id}</RowData>
            <RowData>{marker.label || 'No label'}</RowData>
            <RowData>
              {marker.latitude !== undefined && marker.longitude !== undefined
                ? `${marker.latitude}, ${marker.longitude}`
                : 'No location'}
            </RowData>
            <RowData>
              <DropdownMenu label='Actions' small={true}>
                <DropdownItem onClick={() => deleteMarker(marker.id)}>
                  Delete
                </DropdownItem>
              </DropdownMenu>
            </RowData>
          </Row>
        ))}
      </DataTable>
    </div>
  )
}

function AttachmentsList({ incidentId, attachments, refreshData }) {
  const token = useApiToken()
  const setError = useError()

  const deleteAttachment = useCallback(
    async (id) => {
      try {
        if (
          window.confirm('Are you sure you wish to delete this attachment?')
        ) {
          await deleteDoc(doc(db, 'incidents', incidentId, 'attachments', id))
          refreshData()
        }
      } catch (err) {
        setError(err)
      }
    },
    [setError, refreshData, incidentId]
  )

  return (
    <div>
      <DataTable
        columns={['Name', 'Resource Type', 'Actions']}
        className='is-fullwidth is-hoverable'>
        {attachments?.map((item) => (
          <Row key={item.id}>
            <RowData>{item.name}</RowData>
            <RowData>{item.resource.mimeType}</RowData>
            <RowData>
              <DropdownMenu label='Actions' small={true}>
                <DropdownItem
                  onClick={() => openResources(item.resource.url, token)}>
                  View
                </DropdownItem>
                <DropdownItem onClick={() => deleteAttachment(item.id)}>
                  Delete
                </DropdownItem>
              </DropdownMenu>
            </RowData>
          </Row>
        ))}
      </DataTable>
    </div>
  )
}

function DispatchRow({ item }) {
  const { user, loading } = useUser(item.userId)

  return (
    <Row key={item.userId || Math.random()}>
      <RowData>
        {loading ? <Spinner /> : user?.displayName || 'Unknown User'}
      </RowData>
      <RowData>{item.userId ? 'Active' : 'Cancel'}</RowData>
      <RowData>
        <DateTime value={item.timestamp} />
      </RowData>
    </Row>
  )
}

function IncidentActivity({ incidentId }) {
  const [dispatches, setDispatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [userMap, setUserMap] = useState({})

  useEffect(() => {
    const fetchIncidentData = async () => {
      try {
        const docRef = doc(db, 'incidents', incidentId)
        const docSnap = await getDoc(docRef)

        if (docSnap.exists()) {
          const incidentData = docSnap.data()

          // Safely handle the dispatch data
          let dispatchData = []
          if (Array.isArray(incidentData.acceptedBy)) {
            dispatchData = incidentData.acceptedBy
          } else if (
            incidentData.acceptedBy &&
            typeof incidentData.acceptedBy === 'object'
          ) {
            dispatchData = Object.values(incidentData.acceptedBy)
          }

          setDispatches(dispatchData)

          // fetch user names for each dispatch userId
          // Responders on a dispatched incident are the cross-organisation case
          // by definition. The batched `__name__ in` read was refused outright
          // when any one of them was outside our bindings, so the whole list
          // went blank rather than just that row.
          const userIds = dispatchData.map((d) => d.userId).filter(Boolean)
          if (userIds.length > 0) {
            const byId = await fetchUserProfiles(userIds)
            const map = {}
            userIds.forEach((id) => {
              map[id] = byId[id]?.displayName || byId[id]?.name || 'Unknown User'
            })
            setUserMap(map)
          }
        } else {
          setError('Incident not found')
        }
      } catch (err) {
        console.error('Error fetching incident:', err)
        setError('Failed to load dispatch data')
      } finally {
        setLoading(false)
      }
    }

    fetchIncidentData()
  }, [incidentId])

  if (loading) {
    return <Spinner />
  }

  if (error) {
    return <div className='notification is-danger'>{error}</div>
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <h2 className='subtitle'>Responding Units</h2>
        <button
          className='button is-small'
          style={{ marginLeft: '0.5rem', marginBottom: '1.5rem' }}
          onClick={() => window.location.reload()}>
          <i className='fa fa-sync-alt' />
        </button>
      </div>
      <DataTable
        columns={['User', 'Status', 'Last Update']}
        className='is-fullwidth is-hoverable'>
        {dispatches.length > 0 ? (
          dispatches.map((item) => (
            <DispatchRow key={item.userId} item={item} />
          ))
        ) : (
          <Row>
            <RowData colSpan='3'>No responding units found</RowData>
          </Row>
        )}
      </DataTable>
    </>
  )
}

export default function IncidentDetail({ id, onClose }) {
  const [incident, setIncident] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [createdByUser, setCreatedByUser] = useState(null)
  const [attachments, setAttachments] = useState([])
  const navigate = useNavigate()

  const location = useLocation();

  const isRoutePage = location.pathname.includes("/dispatch");


  const user = auth.currentUser

  const { typeName: organizationTypeName, loading: typeLoading } =
    useOrganizationTypeName(incident?.organization?.id)

  const canUserSeeActions = () => {
    if (!incident || !user) return false

    // Check if current user created the incident
    if (incident.createdBy === user.uid) return true

    // Check if user is in acceptedBy
    return incident.acceptedBy?.some((d) => d.userId === user.uid)
  }

  // The creator of an incident dispatched TO us is in another organisation, so
  // this read is legitimately denied whenever we hold no binding to them.
  const fatchUserData = async (userId) => fetchUserProfile(userId)

  useEffect(() => {
    if (!incident || !incident.createdBy) return
      ; (async () => {
        const userData = await fatchUserData(incident.createdBy)
        setCreatedByUser(userData)
      })()
  }, [incident])

  function displayUser(user) {
    if (!user) return 'Unknown User'
    return user.displayName || `User #${user.id}`
  }

  useEffect(() => {
    reloadIncident()
    // const fetchIncident = async () => {
    //   try {
    //     const docRef = doc(db, 'incidents', id)
    //     const docSnap = await getDoc(docRef)

    //     if (docSnap.exists()) {
    //       setIncident({ id: docSnap.id, ...docSnap.data() })
    //     } else {
    //       console.log('No such document!')
    //     }
    //   } catch (error) {
    //     console.error('Error fetching incident:', error)
    //   } finally {
    //     setLoading(false)
    //   }
    // }

    // fetchIncident()
  }, [id])

  const createDataExportJob = useCallback(async () => {
    if (
      !window.confirm('Are you sure you wish to create a new data export job?')
    ) {
      return
    }

    try {
      // await addDoc(collection(db, 'dataExportJobs'), {
      //   format: 'text',
      //   incidentId: id,
      //   createdAt: new Date(),
      //   status: 'pending'
      // })

      // userId now comes from the verified ID token rather than the body, and
      // the environment is the endpoint, so neither is sent any more.
      const headers = await authedJsonHeaders()
      if (!headers) {
        throw new Error('Not signed in')
      }

      const res = await fetch(
        `https://us-central1-live-alarm-v2.cloudfunctions.net/createIncidentReport${ENV_SUFFIX}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            incidentId: id
          })
        }
      )

      const data = await res.json()

      if (!data.success) throw new Error(data.message || 'Request failed')

      if (data.data?.url) {
        const a = document.createElement('a')
        a.href = data.data.url
        a.download = data.data.filename || 'incident-report.pdf'
        document.body.appendChild(a)
        a.click()
        a.remove()
      } else {
        throw new Error('No URL in API response')
      }

      navigate('/data-export/jobs')
    } catch (err) {
      console.error('Download failed:', err)
      alert(`Download failed: ${err.message}`)
    }
  }, [id, navigate])

  const reloadIncident = async () => {
    setLoading(true)
    const docRef = doc(db, 'incidents', id)
    const docSnap = await getDoc(docRef)
    if (docSnap.exists()) {
      setIncident({ id: docSnap.id, ...docSnap.data() })
      const attachmentsRef = collection(db, 'incidents', id, 'attachments')
      const attachmentsSnap = await getDocs(attachmentsRef)
      const attachmentsList = attachmentsSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }))
      setAttachments(attachmentsList)
    }
    setLoading(false)
  }

  if (loading) {
    return <Spinner />
  }

  if (!incident) {
    return <div>Incident not found</div>
  }

  return (
    <div style={{ width: '100%', marginTop: '1rem' }}>
        <div className="flex items-center justify-end gap-2">
      {canUserSeeActions() && (

          <DropdownMenu label="Actions">
            <DropdownItem onClick={() => setEditing(true)}>
              Edit status
            </DropdownItem>

            <DropdownItem
              onClick={() =>
                navigate(`/incidents/attachment/create?incidentId=${incident.id}`)
              }
            >
              Add a new attachment
            </DropdownItem>

            {incident.status !== "ended" && (
              <DropdownItem
                onClick={() =>
                  navigate(
                    `/incidents/add-dispatch-organization?incidentId=${incident.id}`
                  )
                }
              >
                Add responders
              </DropdownItem>
            )}

            {incident.status === "ended" && (
              <DropdownItem onClick={createDataExportJob}>
                Export data
              </DropdownItem>
            )}
          </DropdownMenu>)}


          {isRoutePage && (
            <div className='dispatch-panel-hide'>
              <button className='button' onClick={onClose}>
                <i className='far fa-times-circle' />
              </button>
            </div>
          )}
        </div>
      

      <div className='box'>
        <h2 className='subtitle'>Incident Details</h2>
        {editing && (
          <EditIncidentStatus
            incident={incident}
            onFinishEditing={() => reloadIncident() && setEditing(false)}
          />
        )}
        <Columns slice={3}>
          <Info label='ID'>{incident.id}</Info>
          <Info label='Organization'>
            {incident.organization && (
              <Link to={`/organizations/${incident.organization.id}`}>
                {incident.organization.name}
              </Link>
            )}
          </Info>
          <Info label='Timestamp'>
            <DateTime value={incident.createdAt} />
          </Info>

          {incident.createdBy && (
            <Info label='Created By'>
              <Link to={`/users/${incident.createdBy}`}>
                {displayUser(createdByUser)}
              </Link>
            </Info>
          )}
          <Info label='Name'>{incident.description}</Info>
          {incident.description && incident.description.length !== 0 && (
            <Info label='Description'>{incident.description}</Info>
          )}
          <Info label='Status'>{capitalize(incident.status)}</Info>
          {/* <Info label='Type'>
            {typeLoading ? 'Loading...' : capitalize(organizationTypeName)}
          </Info> */}
          <Info label='Type'>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center'
              }}>
              <IconPickerItem
                icon={organizationTypeName?.icon}
                size={24}
                color={organizationTypeName?.color}
              />
              {typeLoading
                ? 'Loading...'
                : capitalize(organizationTypeName?.name)}
            </div>
          </Info>
          <Info label='Acknowledged By'>
            {(!incident.acknowledgements ||
              incident.acknowledgements.length === 0) &&
              'N/A'}
            {incident.acknowledgements &&
              incident.acknowledgements.length !== 0 && (
                <Link to={`/users/${incident.acknowledgements[0].user.id}`}>
                  {displayUser(incident.acknowledgements[0].user)}
                </Link>
              )}
          </Info>
          {incident.site && (
            <Info label='Site'>
              <Link to={`/sites/${incident.site.id}`}>
                {incident.site.name}
              </Link>
            </Info>
          )}
          {incident.locations &&
            incident.locations.map((location, index) => (
              <LocationRenderer key={index} location={location} />
            ))}
          {incident.customData &&
            incident.customData.map(({ type: { displayName }, value, id }) => (
              <Info key={id} label={displayName}>
                {typeof value === 'string' ? value : JSON.stringify(value)}
              </Info>
            ))}
        </Columns>
      </div>
      <div className='box'>
        <h2 className='subtitle'>Dispatch Organizations</h2>
        <DispatchOrganizationsList
          dispatchOrganizations={incident.dispatchOrganizations || []}
        />
      </div>
      <div className='box'>
        <IncidentActivity incidentId={incident.id} />
      </div>
      <div className='box'>
        <h2 className='subtitle'>Attachments</h2>
        <AttachmentsList
          incidentId={incident.id}
          // attachments={incident.attachments || []}
          attachments={attachments}
          refreshData={reloadIncident}
        />
      </div>
      <div className='box'>
        <h2 className='subtitle'>Map Markers</h2>
        <MapMarkersList
          incidentId={incident.id}
          mapMarkers={incident.mapMarkers || []}
          refreshData={reloadIncident}
        />
      </div>
    </div>
  )
}
